import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useParams, useNavigate , useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { Icon } from '../components/Icon.js';
import { IconButton } from '../components/ui/IconButton.js';
import { Recorrido } from '../components/ui/Recorrido.js';
import { useRecorrido } from '../hooks/useRecorrido.js';
import { FilterRow } from '../components/ui/FilterRow.js';
import { PillGroup, Pill } from '../components/PillGroup.js';
import { SegmentedControl } from '../components/ui/SegmentedControl.js';
import { TimeBandedList } from '../components/common/TimeBandedList.js';
import { ListItem } from '../components/ui/ListItem.js';
import { ListToolbar } from '../components/ui/ListToolbar.js';
import { SelectionActionBar } from '../components/ui/SelectionActionBar.js';
import { RecurringChargesPanel } from '../components/billing/RecurringChargesPanel.js';
import { StripeTestModeBanner } from '../components/ui/StripeTestModeBanner.js';
import { usePaymentCapability } from '../hooks/useClientPayments.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { Button } from '../components/ui/Button.js';
import { useConfirm } from '../components/ui/ConfirmDialog.js';
import { useToast } from '../components/ui/Toast.js';
import { formatMoney } from '../lib/money.js';
import { EstimateDetail, ESTIMATE_STATUS_TONES } from '../components/billing/EstimateDetail.js';
import { useComposeEstimateSend } from '../hooks/useEstimateOutreach.js';
import { useComposeInvoiceSend, useComposeInvoiceReminder, useMarkInvoicePaid } from '../hooks/useInvoiceOutreach.js';
import { displayInvoiceStatus, necesitaSeguimiento } from '../lib/billingStatus.js';
import { InvoiceDetail } from '../components/billing/InvoiceDetail.js';
import { BillingClaudePanel } from '../components/billing/BillingClaudePanel.js';
import { CreateEstimateModal } from '../components/billing/CreateEstimateModal.js';
import {
  useEstimates, useEstimate, useInvoices, useInvoice, useAccounts,
  useBulkDeleteEstimates, useBulkDeleteInvoices, useConvertEstimate, useMarkEstimateAccepted,
  useDuplicateEstimate,
  type Estimate, type Invoice,
} from '../hooks/useBilling.js';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(value: number): string {
  return new Date(value).toLocaleDateString('es', { year: 'numeric', month: 'short', day: 'numeric' });
}

type DocType = 'estimate' | 'invoice';
// La vista de la lista. Los cobros recurrentes no son un tipo de documento —
// son el acuerdo que EMITE documentos — así que van como vista aparte y no
// como un DocType más.
type BillingView = DocType | 'recurring';
type DocRow = (Estimate & { docType: 'estimate' }) | (Invoice & { docType: 'invoice' });

const ALL_BANDS = new Set(['today', 'yesterday', 'this_week', 'this_month', 'older_2_3_months', 'older']);

// ── Detail header ──────────────────────────────────────────────────────────────
// Copied verbatim from ReportsPage.tsx's DetailPanel "Action row" (not a shared
// component in that file either — it's inline JSX there too, so this replicates
// the same markup/tokens rather than importing a component that doesn't exist as
// an extracted unit). Same title typography (--evo-fs-body-lg / --evo-fw-bold /
// --evo-fg-1), same type-badge span, same Pill usage, same action-row shape with
// plain <Button> (this row lives outside any DetailSection, so REGLA #15's
// SectionHeaderAction-only rule — which is specific to DetailSection's titleRight
// — doesn't apply here; Reports' own header uses plain Button for the same reason).
// Reports has no separate "client" element in this header — for report types like
// web_audit, the subject is baked directly into report.title ("Web audit — domain.com").
// Billing mirrors that by composing the title the same way: "{folio} — {client}".

function DetailHeader({ title, typeLabel, status, statusNamespace = 'estimateStatus', actions }: { title: string; typeLabel: string; status: string; statusNamespace?: 'estimateStatus' | 'invoiceStatus'; actions: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div style={{ height: 'var(--evo-toolbar-height)', padding: '0 var(--evo-content-pad-x) 0 var(--evo-space-5)', borderBottom: '1px solid var(--evo-stroke)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
        <span style={{ fontSize: 'var(--evo-fs-body-lg)', fontWeight: 'var(--evo-fw-bold)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title}
        </span>
        <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '10px', background: 'var(--evo-bg-3)', border: '1px solid var(--evo-stroke)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', fontWeight: 500, flexShrink: 0 }}>
          {typeLabel}
        </span>
        <Pill tone={ESTIMATE_STATUS_TONES[status] ?? 'neutral'} size="xs">
          {t(`billing.${statusNamespace}.${status}`, { defaultValue: status })}
        </Pill>
      </div>
      <div style={{ display: 'flex', gap: 'var(--evo-space-2)', flexShrink: 0, alignItems: 'center' }}>
        {actions}
      </div>
    </div>
  );
}

// ── List panel ─────────────────────────────────────────────────────────────────
// Mirrors ReportsListPanel (pages/ReportsPage.tsx) exactly: ListToolbar for
// select-all/expand-all, TimeBandedList + per-row ListItem selection wired to
// the page-root SelectionActionBar. Both estimates AND invoices support bulk
// selection identically — same checkbox, same toolbar, same action bar. No
// per-view special-casing.

interface BillingListPanelProps {
  view: DocType;
  rows: DocRow[];
  isLoading: boolean;
  selected: { type: DocType; id: number } | null;
  onSelect: (type: DocType, id: number) => void;
  selectedIds: Set<number>;
  onToggleId: (id: number, sel: boolean) => void;
  onSelectAllVisible: (ids: number[]) => void;
  onClearSelection: () => void;
  currencyForAccount: (accountId: number) => string;
}

function BillingListPanel({ view, rows, isLoading, selected, onSelect, selectedIds, onToggleId, onSelectAllVisible, onClearSelection, currencyForAccount }: BillingListPanelProps) {
  const { t } = useTranslation();
  const [collapsedBands, setCollapsedBands] = useState<Set<string>>(new Set());

  return (
    <div style={{ width: 'clamp(300px, 33%, 480px)', flexShrink: 0, borderRight: '1px solid var(--evo-stroke)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <ListToolbar
        allSelected={rows.length > 0 && rows.every(r => selectedIds.has(r.id))}
        someSelected={selectedIds.size > 0 && !rows.every(r => selectedIds.has(r.id))}
        onSelectAll={sel => {
          if (sel) onSelectAllVisible(rows.map(r => r.id));
          else onClearSelection();
        }}
        onExpandAll={() => setCollapsedBands(new Set())}
        onCollapseAll={() => setCollapsedBands(new Set(ALL_BANDS))}
        expandLabel={t('email.expandAll')}
        collapseLabel={t('email.collapseAll')}
      />

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {isLoading && (
          <div style={{ padding: 'var(--evo-space-4)' }}><LoadingState size="sm" /></div>
        )}
        {!isLoading && rows.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 'var(--evo-space-6) var(--evo-space-4)', textAlign: 'center' }}>
            <Icon name="activity" size={24} />
            <p style={{ fontSize: '13px', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', margin: '8px 0 0' }}>
              {view === 'estimate' ? t('billing.facturacion.noEstimates') : t('billing.facturacion.noInvoices')}
            </p>
          </div>
        )}
        {!isLoading && rows.length > 0 && (
          <TimeBandedList<DocRow>
            items={rows}
            getId={r => r.id}
            getTimestamp={r => new Date(r.createdAt).toISOString()}
            collapsedBands={collapsedBands}
            onToggleBand={band => setCollapsedBands(prev => {
              const next = new Set(prev);
              if (next.has(band)) next.delete(band); else next.add(band);
              return next;
            })}
            checkedIds={selectedIds}
            onBandCheck={(ids, allChecked) => { ids.forEach(id => onToggleId(id, !allChecked)); }}
            renderItem={row => {
              const currency = row.docType === 'estimate' ? currencyForAccount(row.accountId) : row.currency;
              const isActive = selected?.type === row.docType && selected.id === row.id;
              // Invoices: 'overdue' derivado en display (billingStatus.ts) — DB sigue 'sent'.
              const displayStatus = row.docType === 'invoice'
                ? displayInvoiceStatus({ status: row.status, dueDate: (row as Invoice).dueDate })
                : row.status;
              return (
                <ListItem
                  key={`${row.docType}-${row.id}`}
                  itemId={`${row.docType}-${row.id}`}
                  tourId={row.id === rows[0]?.id ? 'billing-fila' : undefined}
                  title={row.number ?? `#${row.id}`}
                  subtitle={row.contactName ?? t('billing.facturacion.unknownClient')}
                  meta={`${formatMoney(row.total, currency)} · ${formatDate(row.createdAt)}`}
                  badges={[
                    <Pill key="status" tone={ESTIMATE_STATUS_TONES[displayStatus] ?? 'neutral'} size="xs">
                      {t(`billing.${row.docType === 'estimate' ? 'estimateStatus' : 'invoiceStatus'}.${displayStatus}`, { defaultValue: displayStatus })}
                    </Pill>,
                  ]}
                  selected={selectedIds.has(row.id)}
                  onSelect={(sel: boolean) => onToggleId(row.id, sel)}
                  // Barra cian en la factura que pide seguimiento: enviada, sin
                  // pagar y con más de una semana encima. Mismo criterio que el
                  // indicador del menú, y dura hasta que se cobre — no hasta
                  // que pases por la pantalla. Los presupuestos no aplican.
                  unread={row.docType === 'invoice' && necesitaSeguimiento(row as Invoice)}
                  active={isActive}
                  onClick={() => onSelect(row.docType, row.id)}
                  density="default"
                />
              );
            }}
          />
        )}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export function BillingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { type: typeParam, id: idParam } = useParams<{ type?: string; id?: string }>();
  const parsedId = idParam ? parseInt(idParam) : NaN;
  const selected = (typeParam === 'estimate' || typeParam === 'invoice') && Number.isFinite(parsedId)
    ? { type: typeParam as DocType, id: parsedId }
    : null;

  const [view, setView] = useState<BillingView>(selected?.type ?? 'estimate');
  // V2: vista previa del documento embebida (hoja blanca sobre --evo-doc-backdrop)
  const [docPreview, setDocPreview] = useState(false);
  useEffect(() => { setDocPreview(false); }, [selected?.type, selected?.id]);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  // “+” de Relacionados (2026-08-29): ?nuevo=1&contactId&contactName abre el
  // flujo de crear con el cliente precargado y limpia los params.
  const [precargado, setPrecargado] = useState<{ id: number; name: string } | null>(null);
  useEffect(() => {
    if (searchParams.get('nuevo') !== '1') return;
    const cid = parseInt(searchParams.get('contactId') ?? '');
    const cname = searchParams.get('contactName') ?? '';
    if (Number.isFinite(cid)) setPrecargado({ id: cid, name: cname || `#${cid}` });
    setShowCreateModal(true);
    const next = new URLSearchParams(searchParams);
    next.delete('nuevo'); next.delete('contactId'); next.delete('contactName');
    setSearchParams(next, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [searchParams, setSearchParams] = useSearchParams();
  const { data: accountsData } = useAccounts();
  const { data: estimatesData, isLoading: estimatesLoading } = useEstimates();
  const { data: invoicesData, isLoading: invoicesLoading } = useInvoices();
  const bulkDelete = useBulkDeleteEstimates();
  const bulkDeleteInvoices = useBulkDeleteInvoices();
  const convertEstimate = useConvertEstimate();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const toast = useToast();
  // For the detail header (title/type/status) — same queryKey EstimateDetail/
  // InvoiceDetail themselves use internally, so React Query dedupes this, no
  // extra request. Fetched by id directly (not derived via .find() over the
  // bulk list) — a .find() over invoicesData silently returns undefined
  // whenever the clicked invoice isn't in the currently-loaded list array
  // (filtered view, timing, or a stale/incomplete fetch), which is exactly
  // why the invoice detail could fail to load on click while estimates
  // (already fetched by id) never had that problem.
  const { data: selectedEstimate } = useEstimate(selected?.type === 'estimate' ? selected.id : undefined);
  const { data: selectedInvoice } = useInvoice(selected?.type === 'invoice' ? selected.id : undefined);
  // Sentinel (-1) when nothing is selected yet — same convention EstimateDetail
  // itself uses for useInvoices({ estimateId }) above; harmless for a mutation
  // since it only fires from the button's onClick, by which point
  // selectedEstimate is guaranteed loaded (button only renders when truthy).
  const composeEstimateSend = useComposeEstimateSend(selectedEstimate?.id ?? -1);
  const duplicarCotizacion = useDuplicateEstimate();
  const composeInvoiceSend = useComposeInvoiceSend(selectedInvoice?.id ?? -1);
  const composeInvoiceReminder = useComposeInvoiceReminder(selectedInvoice?.id ?? -1);
  const markInvoicePaid = useMarkInvoicePaid(selectedInvoice?.id ?? -1);
  const markEstimateAccepted = useMarkEstimateAccepted();
  // "Ver factura" on a converted estimate — same estimateId-scoped lookup
  // EstimateDetail uses internally (React Query dedupes it).
  const { data: convertedInvoicesData } = useInvoices({ estimateId: selected?.type === 'estimate' ? selected.id : -1 });
  const convertedInvoice = convertedInvoicesData?.invoices[0];

  const rows: DocRow[] = useMemo(() => {
    if (view === 'estimate') return (estimatesData?.estimates ?? []).map(e => ({ ...e, docType: 'estimate' as const }));
    if (view === 'invoice') return (invoicesData?.invoices ?? []).map(i => ({ ...i, docType: 'invoice' as const }));
    return [];
  }, [view, estimatesData, invoicesData]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      const folio = (r.number ?? `#${r.id}`).toLowerCase();
      const client = (r.contactName ?? '').toLowerCase();
      return folio.includes(q) || client.includes(q);
    });
  }, [rows, search]);

  function currencyForAccount(accountId: number): string {
    return accountsData?.accounts.find(a => a.id === accountId)?.currency ?? 'usd';
  }

  const { data: capability } = usePaymentCapability();

  const { setOverride } = useTopBarCtx();
  useEffect(() => {
    setOverride({
      title: t('billing.facturacion.pageTitle'),
      heroIcon: 'billing',
      heroIconColor: 'var(--evo-steel)',
      bottomActions: (
        <div data-tour-id="billing-nuevo" style={{ display: 'flex' }}>
          <PillGroup size="md">
            <Pill tone="cyan" onClick={() => setShowCreateModal(true)}>
              {t('billing.facturacion.newButton')}
            </Pill>
          </PillGroup>
        </div>
      ),
    });
    return () => setOverride({});
  }, [setOverride, t]);

  // Los ERRORES ya los muestra el manejador global (main.tsx, MutationCache):
  // definir un onError acá lo desactivaría para estas mutaciones. Lo que
  // faltaba era el aviso de ÉXITO.
  //
  // El borrador aparece en ESTE MISMO detalle, en la tarjeta Correos: por eso
  // el aviso ya no manda a buscarlo al historial del contacto — antes se
  // creaba algo que había que ir a encontrar a otra pantalla.
  function avisarBorrador(_contactId: number | undefined, r: { existing?: boolean }) {
    toast.success(r.existing ? t('billing.facturacion.draftExisting') : t('billing.facturacion.draftCreated'));
  }

  function handleSelect(type: DocType, id: number) {
    navigate(`/facturacion/${type}/${id}`);
  }

  function handleCreated(id: number) {
    setShowCreateModal(false);
    setView('estimate');
    navigate(`/facturacion/estimate/${id}`);
  }

  async function handleConvert(id: number) {
    const ok = await confirm({
      title: t('billing.facturacion.convertConfirmTitle'),
      description: t('billing.facturacion.convertConfirmDescription'),
      confirmLabel: t('billing.facturacion.convertToInvoice'),
      tone: 'cyan',
    });
    if (!ok) return;
    convertEstimate.mutate(id, {
      onSuccess: invoice => navigate(`/facturacion/invoice/${invoice.id}`),
    });
  }

  async function handleMarkPaid(_id: number) {
    const ok = await confirm({
      title: t('billing.facturacion.markPaidConfirmTitle'),
      description: t('billing.facturacion.markPaidConfirmDescription'),
      confirmLabel: t('billing.facturacion.markPaid'),
      tone: 'cyan',
    });
    if (!ok) return;
    markInvoicePaid.mutate();
  }

  async function handleDeleteDoc(type: DocType, id: number) {
    const ok = await confirm({
      title: t('billing.facturacion.deleteConfirmTitle'),
      description: t('billing.facturacion.deleteConfirmDescription'),
      confirmLabel: t('billing.facturacion.deleteDoc'),
      tone: 'danger',
    });
    if (!ok) return;
    const mutation = type === 'estimate' ? bulkDelete : bulkDeleteInvoices;
    mutation.mutate([id], { onSuccess: () => navigate('/facturacion') });
  }

  function handleChangeView(v: BillingView) {
    setView(v);
    setSelectedIds(new Set());
    navigate('/facturacion');
  }

  function handleToggleId(id: number, sel: boolean) {
    setSelectedIds(prev => { const next = new Set(prev); if (sel) next.add(id); else next.delete(id); return next; });
  }

  function handleSelectAllVisible(ids: number[]) {
    setSelectedIds(new Set(ids));
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  // ANTES del return: un hook debajo de un return condicional cambia la
  // cantidad de hooks entre renders y React tira la pantalla entera.
  const recorrido = useRecorrido('billing', { hayDatos: rows.length > 0, listo: !estimatesLoading && !invoicesLoading });

  return (
    <div className="flex flex-col h-full animate-fade-blur-in">
      <Recorrido
        pasos={recorrido.pasos}
        abierto={recorrido.abierto}
        onCerrar={recorrido.cerrar}
        onTerminar={recorrido.terminar}
      />
      {/* Arriba de todo y sin cerrar: si esta pantalla muestra montos que no
          se están cobrando de verdad, hay que verlo antes que los montos. */}
      <StripeTestModeBanner visible={capability?.stripeMode === 'test'} />

      <FilterRow>
        <div data-tour-id="billing-buscar" style={{ display: 'flex' }}>
        <FilterRow.Search
          value={search}
          onChange={setSearch}
          onSubmit={() => {}}
          onClear={() => setSearch('')}
          placeholder={t('billing.facturacion.searchPlaceholder')}
        />
        </div>
        <div data-tour-id="billing-vista" style={{ flexShrink: 0, marginLeft: 'auto' }}>
          <SegmentedControl<BillingView>
            value={view}
            onChange={handleChangeView}
            size="sm"
            options={[
              { value: 'estimate', label: t('billing.facturacion.viewEstimates') },
              { value: 'invoice', label: t('billing.facturacion.viewInvoices') },
              { value: 'recurring', label: t('billing.facturacion.viewRecurring') },
            ]}
          />
        </div>
      </FilterRow>

      {/* Solo en las vistas de documentos. En 'recurring' las filas no son
          documentos y la única acción de esta barra es borrar: seleccionar un
          cobro recurrente y apretar borrar habría llamado a bulkDeleteInvoices
          con el id del ACUERDO, borrando la factura que tuviera ese número. */}
      <SelectionActionBar
        visible={view !== 'recurring' && selectedIds.size > 0}
        countLabel={t('billing.facturacion.selected', { count: selectedIds.size })}
        onClearSelection={clearSelection}
      >
        <SelectionActionBar.Action
          label={t('billing.facturacion.bulkDelete')}
          tone="danger"
          confirmRequired
          loading={view === 'estimate' ? bulkDelete.isPending : bulkDeleteInvoices.isPending}
          onClick={() => {
            const ids = Array.from(selectedIds);
            const mutation = view === 'estimate' ? bulkDelete : bulkDeleteInvoices;
            mutation.mutate(ids, {
              onSuccess: () => {
                if (selected?.type === view && selectedIds.has(selected.id)) navigate('/facturacion');
                clearSelection();
              },
            });
          }}
        />
      </SelectionActionBar>

      <div data-tour-id="billing-lista" style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {view === 'recurring' ? (
          // Lista única full-width, no split-pane: un acuerdo recurrente son
          // cinco datos y un panel de detalle al lado quedaría vacío.
          <RecurringChargesPanel />
        ) : (
        <>
        <BillingListPanel
          view={view as DocType}
          rows={filteredRows}
          isLoading={view === 'estimate' ? estimatesLoading : invoicesLoading}
          selected={selected}
          onSelect={handleSelect}
          selectedIds={selectedIds}
          onToggleId={handleToggleId}
          onSelectAllVisible={handleSelectAllVisible}
          onClearSelection={clearSelection}
          currencyForAccount={currencyForAccount}
        />

        {selected ? (
          // Mirrors DetailPanel's outer wrapper (ReportsPage.tsx) exactly —
          // flex:1 + minWidth:0 so this column actually claims the space
          // between the list and the fixed-width assistant panel, instead
          // of sizing to content and displacing the assistant. DetailHeader
          // + the 20px 24px scroll-body padding below are Reports' own
          // action-row/detail-body markup, copied exactly.
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
            {selected.type === 'estimate' ? (
              selectedEstimate && (
                <DetailHeader
                  title={`${selectedEstimate.number ?? `#${selectedEstimate.id}`} — ${selectedEstimate.contactName ?? t('billing.facturacion.unknownClient')}`}
                  typeLabel={t('billing.facturacion.typeEstimate')}
                  status={selectedEstimate.status}
                  actions={
                    // Orden y tonos del diseño: [ver/editar] [extras por
                    // status] [convertir=cian] [enviar] [descargar]
                    // [eliminar=rojo] [cerrar]. Convertir guía el paso
                    // siguiente, nunca bloquea.
                    <>
                      <IconButton
                        size="md" variant="outline" tone="muted"
                        icon={<Icon name={docPreview ? 'pencil' : 'eye'} size={15} />}
                        ariaLabel={docPreview ? t('billing.preview.edit') : t('billing.preview.view')}
                        tooltip={docPreview ? t('billing.preview.edit') : t('billing.preview.view')}
                        onClick={() => setDocPreview(v => !v)}
                      />
                      {selectedEstimate.status === 'sent' && (
                        <IconButton
                          size="md" variant="outline" tone="muted"
                          icon={<Icon name="check" size={15} />}
                          ariaLabel={t('billing.facturacion.markAccepted')} tooltip={t('billing.facturacion.markAccepted')}
                          onClick={() => markEstimateAccepted.mutate(selectedEstimate.id)}
                        />
                      )}
                      {selectedEstimate.status !== 'converted' && (
                        <IconButton
                          size="md" variant="outline" tone="cyan"
                          icon={<Icon name="invoice" size={15} />}
                          ariaLabel={t('billing.facturacion.convertToInvoice')} tooltip={t('billing.facturacion.convertToInvoice')}
                          onClick={() => { void handleConvert(selectedEstimate.id); }}
                        />
                      )}
                      {selectedEstimate.status === 'converted' && convertedInvoice && (
                        <IconButton
                          size="md" variant="outline" tone="cyan"
                          icon={<Icon name="external" size={15} />}
                          ariaLabel={t('billing.facturacion.viewInvoice')} tooltip={t('billing.facturacion.viewInvoice')}
                          onClick={() => navigate(`/facturacion/invoice/${convertedInvoice.id}`)}
                        />
                      )}
                      {/* Duplicar. Es la salida para una ACEPTADA, que no se
                          edita porque es un acuerdo, y sirve igual para armar
                          una variante sin perder la anterior. */}
                      <IconButton
                        size="md" variant="outline" tone="muted"
                        icon={<Icon name="copy" size={15} />}
                        ariaLabel={t('billing.estimates.duplicate')}
                        tooltip={t('billing.estimates.duplicate')}
                        onClick={() => duplicarCotizacion.mutate(selectedEstimate.id, {
                          onSuccess: copia => navigate(`/facturacion/estimate/${copia.id}`),
                        })}
                      />
                      {(selectedEstimate.status === 'draft' || selectedEstimate.status === 'sent' || selectedEstimate.status === 'accepted') && (
                        <IconButton
                          size="md" variant="outline" tone="muted"
                          icon={<Icon name="send" size={15} />}
                          ariaLabel={t('billing.preview.send')} tooltip={t('billing.preview.send')}
                          onClick={() => composeEstimateSend.mutate(undefined, {
                            onSuccess: r => avisarBorrador(selectedEstimate.contactId, r),
                          })}
                        />
                      )}
                      <a href={`/api/billing/estimates/${selectedEstimate.id}/pdf`} download style={{ display: 'inline-flex' }}>
                        <IconButton
                          size="md" variant="outline" tone="muted"
                          icon={<Icon name="download" size={15} />}
                          ariaLabel={t('billing.preview.download')} tooltip={t('billing.preview.download')}
                        />
                      </a>
                      <IconButton
                        size="md" variant="outline" tone="danger"
                        icon={<Icon name="trash" size={15} />}
                        ariaLabel={t('billing.facturacion.deleteDoc')} tooltip={t('billing.facturacion.deleteDoc')}
                        onClick={() => { void handleDeleteDoc('estimate', selectedEstimate.id); }}
                      />
                      <IconButton
                        size="md" variant="ghost" tone="muted"
                        icon={<Icon name="close" size={14} />}
                        ariaLabel={t('common.close')}
                        onClick={() => navigate('/facturacion')}
                      />
                    </>
                  }
                />
              )
            ) : (
              selectedInvoice && (
                <DetailHeader
                  title={`${selectedInvoice.number ?? `#${selectedInvoice.id}`} — ${selectedInvoice.contactName ?? t('billing.facturacion.unknownClient')}`}
                  typeLabel={t('billing.facturacion.typeInvoice')}
                  status={displayInvoiceStatus(selectedInvoice)}
                  statusNamespace="invoiceStatus"
                  actions={
                    // 'overdue' es estado derivado de display
                    // (billingStatus.ts) — la DB sigue en 'sent', por eso las
                    // condiciones chequean el status real. Orden del diseño.
                    <>
                      <IconButton
                        size="md" variant="outline" tone="muted"
                        icon={<Icon name={docPreview ? 'pencil' : 'eye'} size={15} />}
                        ariaLabel={docPreview ? t('billing.preview.edit') : t('billing.preview.view')}
                        tooltip={docPreview ? t('billing.preview.edit') : t('billing.preview.view')}
                        onClick={() => setDocPreview(v => !v)}
                      />
                      {selectedInvoice.status === 'sent' && (
                        <>
                          <IconButton
                            size="md" variant="outline" tone="muted"
                            icon={<Icon name="bell" size={15} />}
                            ariaLabel={t('billing.facturacion.paymentReminder')} tooltip={t('billing.facturacion.paymentReminder')}
                            onClick={() => composeInvoiceReminder.mutate(undefined, {
                              onSuccess: r => avisarBorrador(selectedInvoice.contactId, r),
                            })}
                          />
                          <IconButton
                            size="md" variant="outline" tone="cyan"
                            icon={<Icon name="check" size={15} />}
                            ariaLabel={t('billing.facturacion.markPaid')} tooltip={t('billing.facturacion.markPaid')}
                            onClick={() => { void handleMarkPaid(selectedInvoice.id); }}
                          />
                        </>
                      )}
                      {(selectedInvoice.status === 'draft' || selectedInvoice.status === 'sent') && (
                        <IconButton
                          size="md" variant="outline" tone="muted"
                          icon={<Icon name="send" size={15} />}
                          ariaLabel={t(selectedInvoice.status === 'draft' ? 'billing.facturacion.sendToClient' : 'billing.facturacion.resendToClient')}
                          tooltip={t(selectedInvoice.status === 'draft' ? 'billing.facturacion.sendToClient' : 'billing.facturacion.resendToClient')}
                          onClick={() => composeInvoiceSend.mutate(undefined, {
                            onSuccess: r => avisarBorrador(selectedInvoice.contactId, r),
                          })}
                        />
                      )}
                      <a href={`/api/billing/invoices/${selectedInvoice.id}/pdf`} download style={{ display: 'inline-flex' }}>
                        <IconButton
                          size="md" variant="outline" tone="muted"
                          icon={<Icon name="download" size={15} />}
                          ariaLabel={t('billing.preview.download')} tooltip={t('billing.preview.download')}
                        />
                      </a>
                      <IconButton
                        size="md" variant="outline" tone="danger"
                        icon={<Icon name="trash" size={15} />}
                        ariaLabel={t('billing.facturacion.deleteDoc')} tooltip={t('billing.facturacion.deleteDoc')}
                        onClick={() => { void handleDeleteDoc('invoice', selectedInvoice.id); }}
                      />
                      <IconButton
                        size="md" variant="ghost" tone="muted"
                        icon={<Icon name="close" size={14} />}
                        ariaLabel={t('common.close')}
                        onClick={() => navigate('/facturacion')}
                      />
                    </>
                  }
                />
              )
            )}
            {docPreview ? (
              // El documento (doc-page) pinta su propio escritorio, hoja y
              // sombra — el iframe solo ocupa el área, sin caja duplicada.
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <iframe
                  src={`/api/billing/${selected.type === 'estimate' ? 'estimates' : 'invoices'}/${selected.id}/preview`}
                  title="doc-preview"
                  style={{ display: 'block', width: '100%', height: '100%', border: 'none' }}
                />
              </div>
            ) : (
              <div style={{ flex: 1, overflow: 'auto', padding: 'var(--evo-pane-pad)' }}>
                {selected.type === 'estimate' ? (
                  <EstimateDetail
                    id={selected.id}
                    accounts={accountsData?.accounts ?? []}
                    clientName={selectedEstimate?.contactName ?? undefined}
                  />
                ) : (
                  <InvoiceDetail id={selected.id} clientName={selectedInvoice?.contactName ?? undefined} />
                )}
              </div>
            )}
          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px', color: 'var(--evo-fg-3)', minWidth: 0 }}>
            <Icon name="activity" size={28} />
            <p style={{ fontSize: '13px', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)', margin: 0 }}>{t('billing.facturacion.selectDocument')}</p>
          </div>
        )}

        <BillingClaudePanel docType={selected?.type ?? null} docId={selected?.id ?? null} />
        </>
        )}
      </div>

      {showCreateModal && (
        <CreateEstimateModal
          preselectedContact={precargado}
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
        />
      )}
      {confirmDialog}
    </div>
  );
}
